import dayjs from "dayjs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { EventModalManager } from "@/features/study-group-events/components/EventModalManager";
import { getActivityClickAction } from "@/features/study-group-stream/utils/activityRouting";
import { useActivities } from "@/hooks/useActivities";
import { useBlockedUsersList } from "@/hooks/useBlockedUsersList";
import { useEventByIdOptional } from "@/hooks/useEventById";
import { useEventModalState } from "@/hooks/useEventModalState";
import { useToast } from "@/hooks/useToast";
import type { ActivityResponse } from "@/lib/humhub-api/types";
import "dayjs/locale/en";
import "dayjs/locale/ja";
import { useNavigate, useParams } from "@tanstack/react-router";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Initialize dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const getRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : null;

const getString = (value: unknown): string | null =>
  typeof value === "string" && value.length > 0 ? value : null;

const containsDatePlaceholder = (value: string): boolean =>
  /{{\s*date\s*}}/.test(value);

const formatDisplayDateForUserTimezone = (
  activity: ActivityResponse,
  params: Record<string, unknown>,
): string | null => {
  const sourceRecord = getRecord(activity.source);
  const sourceDateRaw = getString(sourceRecord?.start_datetime);
  const sourceTimeZone = getString(sourceRecord?.time_zone);
  const dateRaw = getString(params.dateRaw) ?? sourceDateRaw;
  const timeZoneRaw = getString(params.timeZoneRaw) ?? sourceTimeZone ?? "UTC";
  if (!dateRaw) return null;

  const localDate = dayjs.tz(dateRaw, timeZoneRaw).local();
  if (!localDate.isValid()) return null;

  const allDayValue = params.allDay ?? sourceRecord?.all_day;
  const isAllDay =
    allDayValue === 1 || allDayValue === true || allDayValue === "1";
  const formatStr = isAllDay ? "YYYY/MM/DD" : "YYYY/MM/DD HH:mm";
  return localDate.format(formatStr);
};

const resolveDisplayText = (
  activity: ActivityResponse,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => {
  const fallbackOutput = getString(activity.content?.output) ?? "";
  const display = activity.display;
  if (!display) {
    return fallbackOutput;
  }

  const displayText = getString(display.text);
  const fallbackText = getString(display.fallbackText) ?? fallbackOutput;

  if (!display.needsClientTimeFormat) {
    return displayText ?? fallbackText;
  }

  const params = getRecord(display.params)
    ? ({ ...display.params } as Record<string, unknown>)
    : {};
  const localizedDate = formatDisplayDateForUserTimezone(activity, params);
  if (localizedDate) {
    params.date = localizedDate;
  }

  const displayKey = getString(display.key);
  if (displayKey) {
    const translated = t(displayKey, params);
    if (
      translated &&
      translated !== displayKey &&
      !containsDatePlaceholder(translated)
    ) {
      return translated;
    }
  }

  if (displayText) {
    const dateText = getString(params.date);
    if (dateText && containsDatePlaceholder(dateText)) {
      return fallbackText;
    }
    if (dateText && containsDatePlaceholder(displayText)) {
      return displayText.replace(/{{\s*date\s*}}/g, dateText);
    }
    if (containsDatePlaceholder(displayText)) {
      return fallbackText;
    }
    return displayText;
  }

  return fallbackText;
};

interface CatchUpListProps {
  containerId?: string | number;
  height?: string;
  studyGroupId?: string | number;
}

type ActivityActor = NonNullable<ActivityResponse["actors"]>[number];
type AvatarLikeUser = Pick<ActivityActor, "display_name" | "guid">;

const getUserInitials = (user: AvatarLikeUser | null | undefined): string => {
  const displayName = user?.display_name || user?.guid || "Unknown User";
  return displayName
    .split(" ")
    .map((name) => name[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);
};

export const CatchUpList: React.FC<CatchUpListProps> = ({
  containerId,
  height = "h-60",
  studyGroupId,
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state: modalState, actions: modalActions } = useEventModalState();
  const [pendingEventId, setPendingEventId] = useState<number | null>(null);
  const { data: pendingEvent, error: pendingEventError } =
    useEventByIdOptional(pendingEventId);
  // We need the study-group-id from the URL for navigation, because containerId is the contentcontainer_id
  const { "study-group-id": studyGroupIdFromParams } = useParams({
    strict: false,
  });
  const groupIdForRoute = studyGroupId ?? studyGroupIdFromParams;

  const { data: blockedUsers = [] } = useBlockedUsersList();
  const blockedUserSet = useMemo(() => {
    if (!Array.isArray(blockedUsers) || blockedUsers.length === 0) {
      return new Set<string>();
    }
    return new Set(blockedUsers.map((id) => String(id)));
  }, [blockedUsers]);

  // Set dayjs locale when language changes
  useEffect(() => {
    const locale = i18n.language.startsWith("ja") ? "ja" : "en";
    dayjs.locale(locale);
  }, [i18n.language]);

  useEffect(() => {
    if (!pendingEventId) return;
    if (pendingEvent) {
      modalActions.openEventDetail(pendingEvent);
      setPendingEventId(null);
      return;
    }
    if (pendingEventError) {
      toast({ description: String(pendingEventError) });
      setPendingEventId(null);
    }
  }, [pendingEventId, pendingEvent, pendingEventError, modalActions, toast]);

  const {
    data: activities,
    isLoading,
    error,
    refetch,
  } = useActivities(containerId, 1, 20);

  const filteredActivities = useMemo(() => {
    if (!Array.isArray(activities) || activities.length === 0) {
      return [] as typeof activities;
    }
    if (blockedUserSet.size === 0) {
      return activities;
    }
    return activities.filter((activity) => {
      const originatorId = activity.originator?.id;
      return !blockedUserSet.has(String(originatorId));
    });
  }, [activities, blockedUserSet]);

  const handleDataRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  const handleItemClick = useCallback(
    (activity: ActivityResponse) => {
      const action = getActivityClickAction(activity, groupIdForRoute);
      if (!action) {
        toast({ description: t("common.noDestination") });
        return;
      }
      if (action.kind === "modal") {
        setPendingEventId(action.eventId);
        return;
      }
      if (action.destination.to === "/event-search/$event-id") {
        const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        navigate({
          ...action.destination,
          search: {
            ...(action.destination.search ?? {}),
            from,
          },
        });
        return;
      }
      navigate(action.destination);
    },
    [groupIdForRoute, navigate, t, toast],
  );

  if (isLoading) {
    return (
      <section className="stream-border-post w-full rounded-[0px_17px_0px_0px] border-2 p-4">
        <h2 className="text-xl font-semibold text-[rgba(86,90,91,1)]">
          {t("common.catchUp")}
        </h2>
        <hr className="stream-border-reply my-2 border-t-2" />
        <div className={`${height} overflow-y-auto`}>
          <div className="space-y-3">
            {["skeleton-1", "skeleton-2", "skeleton-3"].map((key) => (
              <div key={key} className="flex items-center gap-2 px-0 py-2">
                <Loader2 className="size-8 text-[rgba(157,165,171,1)]" />
                <div className="flex flex-1 flex-col">
                  <div className="mb-1 h-4 w-3/4 rounded bg-[rgba(157,165,171,0.3)]" />
                  <div className="h-3 w-1/4 rounded bg-[rgba(157,165,171,0.3)]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full rounded-[0px_17px_0px_0px] p-4">
        <h2 className="text-xl font-semibold text-[rgba(86,90,91,1)]">
          {t("common.catchUp")}
        </h2>
        <hr className="stream-border-reply my-2 border-t-2" />
        <div className={`${height} overflow-y-auto`}>
          <p className="text-red-500">{t("common.failedToLoadActivities")}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="stream-border-post w-full rounded-[0px_17px_0px_0px] border-2 p-4">
        <h2 className="text-xl font-semibold text-[rgba(86,90,91,1)]">
          {t("common.catchUp")}
        </h2>
        <hr className="stream-border-post my-2 border-t-2" />
        <div className={`${height} overflow-y-auto`}>
          {filteredActivities.length === 0 ? (
            <p className="text-sm text-[rgba(86,90,91,1)]">
              {t("common.noRecentActivities")}
            </p>
          ) : (
            <div className="space-y-2 pr-2">
              {filteredActivities.map((activity) => {
                const displayText = resolveDisplayText(activity, t);
                const isGroupedActivity =
                  activity.itemType === "group" && (activity.count ?? 0) >= 2;
                const groupedAvatarUsers = isGroupedActivity
                  ? (Array.isArray(activity.actors)
                      ? activity.actors
                      : []
                    ).slice(0, 2)
                  : [];

                // UTCからローカルタイムゾーンに変換し、相対時間を取得
                const relativeTimeText = (() => {
                  const localTime = dayjs.tz(activity.createdAt, "UTC").local();
                  if (!localTime.isValid()) {
                    return activity.createdAt; // Fallback to original string if invalid
                  }
                  return localTime.fromNow();
                })();

                return (
                  <button
                    key={activity.id}
                    type="button"
                    className="flex w-full cursor-pointer items-start gap-2 rounded-md px-0 py-2 text-left transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
                    onClick={() => handleItemClick(activity)}
                  >
                    <div
                      className={`flex h-8 shrink-0 items-center ${
                        isGroupedActivity && groupedAvatarUsers.length > 1
                          ? "w-14 justify-start"
                          : "w-8 justify-start"
                      }`}
                    >
                      {isGroupedActivity && groupedAvatarUsers.length > 0 ? (
                        groupedAvatarUsers.map((actor, index) => (
                          <Avatar
                            key={actor.id}
                            className={`size-8 ring-2 ring-white ${index > 0 ? "-ml-2" : ""}`}
                          >
                            <AvatarImage
                              src={actor.image_url || ""}
                              alt={actor.display_name}
                            />
                            <AvatarFallback>
                              {getUserInitials(actor)}
                            </AvatarFallback>
                          </Avatar>
                        ))
                      ) : (
                        <Avatar className="size-8">
                          <AvatarImage
                            src={activity.originator?.image_url || ""}
                            alt={activity.originator?.display_name}
                          />
                          <AvatarFallback>
                            {getUserInitials(activity.originator)}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="break-words text-[13px] font-normal text-[rgba(86,90,91,1)]">
                        {displayText}
                      </div>
                      <time className="text-[10px] font-medium text-[rgba(157,165,171,1)]">
                        {relativeTimeText}
                      </time>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>
      <EventModalManager
        state={modalState}
        actions={modalActions}
        onDataRefresh={handleDataRefresh}
      />
    </>
  );
};
